import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { previewRegionMove } from '../../../../api/regionMove';
import {
  fetchRegions,
  fetchMapVenues,
  fetchRegionDetail,
  fetchRegionComponents,
  fetchRegionVenues,
  fetchRegionActivity,
  fetchUnassignedVenues,
  fetchRegionBoundary,
  fetchRegionBoundaries,
  moveRegion,
  renameRegion,
  setRegionDirector,
  setRegionColor,
  createRegion,
  deleteRegion,
  mergeRegion,
  setVenueException,
  triggerGeocoding,
  type RegionNode,
  type MapVenue,
  type RegionDetail,
  type RegionComponent,
  type RegionBoundary,
  type RegionActivityEntry,
  type TriageVenue,
  type AssignmentMode,
} from '../../../../api/regions';

// React Query hooks for the Region Management surfaces (BON-153). The region
// endpoints carry their own scoping server-side, so these are scope-agnostic;
// the surface picks what to render.
const STALE_30S = 30_000;

export function useRegionTree() {
  return useQuery<RegionNode[]>({
    queryKey: ['regions', 'tree'],
    queryFn: fetchRegions,
    staleTime: STALE_30S,
  });
}

export function useMapVenues() {
  return useQuery<MapVenue[]>({
    queryKey: ['regions', 'map-venues'],
    queryFn: fetchMapVenues,
    staleTime: STALE_30S,
  });
}

export function useRegionDetail(regionId: string | null) {
  return useQuery<RegionDetail>({
    queryKey: ['regions', 'detail', regionId],
    queryFn: () => fetchRegionDetail(regionId as string),
    staleTime: STALE_30S,
    enabled: Boolean(regionId),
  });
}

/** Venues that are members of a region (it + all subregions). */
export function useRegionVenues(regionId: string | null) {
  return useQuery<MapVenue[]>({
    queryKey: ['regions', 'venues', regionId],
    queryFn: () => fetchRegionVenues(regionId as string),
    staleTime: STALE_30S,
    enabled: Boolean(regionId),
  });
}

/** Recent activity feed for a region (audit events, newest first). */
export function useRegionActivity(regionId: string | null) {
  return useQuery<RegionActivityEntry[]>({
    queryKey: ['regions', 'activity', regionId],
    queryFn: () => fetchRegionActivity(regionId as string),
    staleTime: STALE_30S,
    enabled: Boolean(regionId),
  });
}

export function useRegionComponents(regionId: string | null) {
  return useQuery<RegionComponent[]>({
    queryKey: ['regions', 'components', regionId],
    queryFn: () => fetchRegionComponents(regionId as string),
    staleTime: STALE_30S,
    enabled: Boolean(regionId),
  });
}

export function useRegionBoundaries() {
  return useQuery<RegionBoundary[]>({
    queryKey: ['regions', 'boundaries'],
    queryFn: fetchRegionBoundaries,
    staleTime: STALE_30S,
  });
}

export function useRegionBoundary(
  regionId: string | null,
): UseQueryResult<GeoJSON.Geometry | null> {
  return useQuery<GeoJSON.Geometry | null>({
    queryKey: ['regions', 'boundary', regionId],
    queryFn: () => fetchRegionBoundary(regionId as string),
    staleTime: STALE_30S,
    enabled: Boolean(regionId),
  });
}

export function useUnassignedVenues() {
  return useQuery<TriageVenue[]>({
    queryKey: ['regions', 'unassigned-venues'],
    queryFn: fetchUnassignedVenues,
    staleTime: STALE_30S,
  });
}

/** Set a venue's region exception (auto/pin/exclude); refreshes region views. */
export function useSetVenueException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { venueId: string; mode: AssignmentMode; regionId?: string | null }) =>
      setVenueException(vars.venueId, vars.mode, vars.regionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}

/** Move a region under a new parent (drag-to-reparent); refreshes the tree. */
export function useMoveRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; parentId: string | null }) =>
      moveRegion(vars.id, vars.parentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}

/** Preview a move. Disabled until a destination is chosen; `parentId: null`
 *  (move to top level) is a real choice, so `enabled` keys off the picker
 *  having been touched rather than off the value being non-null. */
export function useRegionMovePreview(id: string, parentId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['regions', id, 'move-preview', parentId],
    queryFn: () => previewRegionMove(id, parentId),
    enabled,
    staleTime: 0,
  });
}

/** Rename a region; refreshes region views (tree, detail, header). */
export function useRenameRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; name: string }) => renameRegion(vars.id, vars.name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}

/** Assign or clear a region's director; refreshes region views. */
export function useSetRegionDirector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; userId: string | null }) =>
      setRegionDirector(vars.id, vars.userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}

/** Set a root region's display color; refreshes region views (color cascades). */
export function useSetRegionColor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; color: string | null }) => setRegionColor(vars.id, vars.color),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}

/** Create a region (optionally under a parent); refreshes the tree. */
export function useCreateRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { name: string; parentId: string | null }) =>
      createRegion(vars.name, vars.parentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}

/** Permanently delete a region (only safe when it has no venues); refreshes the tree. */
export function useDeleteRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRegion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}

/** Merge a region into another (moves venues + subregions, then archives); refreshes views. */
export function useMergeRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; intoRegionId: string }) =>
      mergeRegion(vars.id, vars.intoRegionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}

/** Re-resolve every auto venue's region from coordinates; refreshes region views. */
export function useTriggerGeocoding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: triggerGeocoding,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions'] }),
  });
}
